/**
 * InputPrompt.ts — Reusable input prompt with auto-generate option.
 *
 * Provides consistent prompt UX for name entry, confirmations, and choices.
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

import type { Terminal } from './Terminal';
import type { InputManager } from './InputManager';
import type { MenuSystem, MenuOption } from './MenuSystem';
import type { TextEffects } from './TextEffects';
import type { ColorName } from './ColorTheme';

// --- Prompt Configuration ---

export interface PromptConfig {
  /** Label shown above the input (e.g., "Hero Name") */
  label: string;
  /** Color of the label */
  labelColor?: ColorName;
  /** Generator function that produces a random value */
  generator: () => string;
  /** Optional validator (returns error message or undefined if valid) */
  validator?: (value: string) => string | undefined;
  /** Minimum length (default 1) */
  minLength?: number;
  /** Maximum length (default 30) */
  maxLength?: number;
  /** Starting row for the prompt display */
  startRow?: number;
  /** Optional description/hint shown below the label */
  hint?: string;
}

// --- InputPrompt ---

export class InputPrompt {
  private terminal: Terminal;
  private inputManager: InputManager;
  private menu: MenuSystem;
  private effects: TextEffects;

  constructor(
    terminal: Terminal,
    inputManager: InputManager,
    menu: MenuSystem,
    effects: TextEffects,
  ) {
    this.terminal = terminal;
    this.inputManager = inputManager;
    this.menu = menu;
    this.effects = effects;
  }

  /** Prompt the player for a value with an auto-generate option. */
  async prompt(config: PromptConfig): Promise<string> {
    const {
      label,
      labelColor = 'yellow',
      validator,
      minLength = 1,
      maxLength = 30,
      startRow = 3,
      hint,
    } = config;

    while (true) {
      this.renderHeader(label, labelColor, startRow, hint);

      const options: MenuOption[] = [
        { label: 'Generate Random', color: 'green' },
        { label: 'Enter Manually', color: 'amber' },
      ];

      const choice = await this.menu.show(options, {
        title: label,
        titleColor: labelColor,
        showExit: true,
        exitLabel: 'Cancel',
        startRow: startRow + (hint ? 3 : 2),
      });

      if (choice === -1) return '';

      let result: string;

      if (choice === 0) {
        result = await this.generateLoop(config);
      } else {
        result = await this.manualEntry(config);
      }

      if (result === '') continue;

      if (result.length < minLength) {
        this.terminal.blankLine();
        this.terminal.writeLine(`  Must be at least ${minLength} character(s).`, 'red');
        await this.effects.delay(1500);
        continue;
      }

      if (result.length > maxLength) {
        this.terminal.blankLine();
        this.terminal.writeLine(`  Must be ${maxLength} characters or fewer.`, 'red');
        await this.effects.delay(1500);
        continue;
      }

      if (validator) {
        const error = validator(result);
        if (error) {
          this.terminal.blankLine();
          this.terminal.writeLine(`  ${error}`, 'red');
          await this.effects.delay(1500);
          continue;
        }
      }

      return result;
    }
  }

  /** Simple text prompt without auto-generate. */
  async textPrompt(config: {
    label: string;
    labelColor?: ColorName;
    hint?: string;
    validator?: (value: string) => string | undefined;
    minLength?: number;
    maxLength?: number;
    startRow?: number;
    defaultValue?: string;
  }): Promise<string> {
    const {
      label,
      labelColor = 'yellow',
      hint,
      validator,
      minLength = 1,
      maxLength = 60,
      startRow = 3,
      defaultValue,
    } = config;

    while (true) {
      this.renderHeader(label, labelColor, startRow, hint);

      if (defaultValue) {
        this.terminal.writeLine(`  Current: ${defaultValue}`, 'frostSilver');
        this.terminal.writeLine('  Press Enter to keep, or type a new value:', 'frostSilver');
      } else {
        this.terminal.writeLine('  Type your answer and press Enter:', 'frostSilver');
      }

      this.terminal.blankLine();
      this.terminal.write('  > ', 'yellow');
      this.terminal.showCursor();

      const text = await this.readTextInput();

      if (text === '' && defaultValue) return defaultValue;
      if (text === '\x1b') return '';

      if (text.length < minLength) {
        this.terminal.blankLine();
        this.terminal.writeLine(`  Must be at least ${minLength} character(s).`, 'red');
        await this.effects.delay(1500);
        continue;
      }

      if (text.length > maxLength) {
        this.terminal.blankLine();
        this.terminal.writeLine(`  Must be ${maxLength} characters or fewer.`, 'red');
        await this.effects.delay(1500);
        continue;
      }

      if (validator) {
        const error = validator(text);
        if (error) {
          this.terminal.blankLine();
          this.terminal.writeLine(`  ${error}`, 'red');
          await this.effects.delay(1500);
          continue;
        }
      }

      return text;
    }
  }

  /** Yes/No confirmation prompt. */
  async confirm(message: string, defaultYes = true): Promise<boolean> {
    const options: MenuOption[] = defaultYes
      ? [
          { label: 'Yes', color: 'green' },
          { label: 'No', color: 'red' },
        ]
      : [
          { label: 'No', color: 'red' },
          { label: 'Yes', color: 'green' },
        ];

    const selection = await this.menu.show(options, {
      title: message,
      titleColor: 'yellow',
      showExit: false,
      startRow: 3,
    });

    return defaultYes ? selection === 0 : selection === 1;
  }

  /** Number prompt — player picks a number within a range. */
  async numberPrompt(config: {
    label: string;
    labelColor?: ColorName;
    min: number;
    max: number;
    defaultValue?: number;
    startRow?: number;
  }): Promise<number> {
    const { label, labelColor = 'yellow', min, max, defaultValue, startRow = 3 } = config;

    while (true) {
      this.renderHeader(label, labelColor, startRow);
      this.terminal.writeLine(`  Enter a number between ${min} and ${max}:`, 'frostSilver');

      if (defaultValue !== undefined) {
        this.terminal.writeLine(`  (Default: ${defaultValue}, press Enter to accept)`, 'frostSilver');
      }

      this.terminal.blankLine();
      this.terminal.write('  > ', 'yellow');
      this.terminal.showCursor();

      const text = await this.readTextInput();

      if (text === '\x1b') return defaultValue ?? min;
      if (text === '' && defaultValue !== undefined) return defaultValue;

      const num = parseInt(text, 10);
      if (isNaN(num) || num < min || num > max) {
        this.terminal.blankLine();
        this.terminal.writeLine(`  Please enter a number between ${min} and ${max}.`, 'red');
        await this.effects.delay(1500);
        continue;
      }

      return num;
    }
  }

  // --- Private Helpers ---

  private async generateLoop(config: PromptConfig): Promise<string> {
    const { label, labelColor = 'yellow', generator, startRow = 3 } = config;

    let generated = generator();

    while (true) {
      this.terminal.clear();
      this.terminal.blankLine();
      this.terminal.writeLine(`  ${label}`, labelColor);
      this.terminal.blankLine();
      this.terminal.write('  Generated: ', 'frostSilver');
      this.terminal.writeLine(generated, 'brightWhite');
      this.terminal.blankLine();

      const options: MenuOption[] = [
        { label: 'Accept', color: 'green' },
        { label: 'Re-roll', color: 'amber' },
        { label: 'Enter Manually Instead', color: 'white' },
      ];

      const choice = await this.menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: startRow + 4,
      });

      switch (choice) {
        case 0: return generated;
        case 1: generated = generator(); continue;
        case 2: return await this.manualEntry(config);
        case -1: return '';
      }
    }
  }

  private async manualEntry(config: PromptConfig): Promise<string> {
    const { label, labelColor = 'yellow', hint } = config;

    this.terminal.clear();
    this.terminal.blankLine();
    this.terminal.writeLine(`  ${label}`, labelColor);

    if (hint) {
      this.terminal.writeLine(`  ${hint}`, 'frostSilver');
    }

    this.terminal.blankLine();
    this.terminal.writeLine('  Type your answer and press Enter (Escape to go back):', 'frostSilver');
    this.terminal.blankLine();
    this.terminal.write('  > ', 'yellow');
    this.terminal.showCursor();

    const text = await this.readTextInput();

    if (text === '\x1b') return '';
    return text.trim();
  }

  private readTextInput(): Promise<string> {
    return new Promise<string>((resolve) => {
      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('text');

      const handler = (event: { type: string; text?: string }) => {
        if (event.type === 'textSubmit') {
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve(event.text ?? '');
        } else if (event.type === 'cancel') {
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve('\x1b');
        }
      };

      this.inputManager.on(handler);
    });
  }

  private renderHeader(
    label: string,
    labelColor: ColorName,
    startRow: number,
    hint?: string,
  ): void {
    this.terminal.clear();
    this.terminal.blankLine();
    this.terminal.setCursor(startRow, 1);
    this.terminal.writeLine(`  ${label}`, labelColor);

    if (hint) {
      this.terminal.writeLine(`  ${hint}`, 'frostSilver');
    }

    this.terminal.blankLine();
  }
}
